import { scrollTourTargetIntoView, closeTopbarClientMenu, openTopbarNavMenu } from './tourElementUtils';

export const DASHBOARD_PATH = '/dietitian-dashboard';
export const CLIENT_DASHBOARD_PATH = '/client-dashboard';
export const CHAT_PATH = '/chat';
export const MEAL_PLANS_PATH = '/client-meal-plans';
export const TRAINING_PATH = '/training-management';
export const FOODLOGS_PATH = '/client-food-logs';
export const ANALYTICS_PATH = '/analytics';
export const MANAGER_DASHBOARD_PATH = '/manager-dashboard';
export const EMPLOYEE_MANAGEMENT_PATH = '/employee-management';

export const ALLOWED_ROUTES = [
  DASHBOARD_PATH,
  CLIENT_DASHBOARD_PATH,
  CHAT_PATH,
  MEAL_PLANS_PATH,
  TRAINING_PATH,
  FOODLOGS_PATH,
  ANALYTICS_PATH,
  MANAGER_DASHBOARD_PATH,
  EMPLOYEE_MANAGEMENT_PATH,
];

const ADMIN_CACHE_KEY = 'adminDashboardCache';

export function readShowAssignments(canSeeCompany) {
  try {
    const raw = sessionStorage.getItem(ADMIN_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.showClientAssignmentsColumn != null) {
        return !!parsed.showClientAssignmentsColumn;
      }
    }
  } catch {
    // ignore
  }
  return !!canSeeCompany;
}

export const WELCOME = {
  english: {
    title: 'Welcome',
    paragraphs: [
      'A short website tour of your daily workflow.',
      'Clients, chat, meal plans, training, food logs and analytics.',
    ],
    gateCta: 'Start Tour',
    videoTitle: 'Your Guide',
    videoParagraphs: [
      'Watch along. Sound is on.',
      'When you are ready, continue into the live product.',
    ],
    cta: 'Continue',
  },
  hebrew: {
    title: 'ברוכים הבאים',
    paragraphs: [
      'סיור קצר באתר על השגרה היומית.',
      'לקוחות, צ׳אט, תוכניות תזונה, אימונים, יומן אכילה ואנליטיקה.',
    ],
    gateCta: 'התחלת הסיור',
    videoTitle: 'המדריך שלכם',
    videoParagraphs: [
      'צפו יחד. הסאונד פעיל.',
      'כשאתם מוכנים, המשיכו למוצר עצמו.',
    ],
    cta: 'המשך',
  },
};

export function buildWelcome(language) {
  return WELCOME[language] || WELCOME.english;
}

// Page-level tour hooks (listened for by the pages themselves).
const fire = (name) => window.dispatchEvent(new Event(name));
const closeReferralDialog = () => fire('portfolio-playbook-close-referral');
const closePricingDrawer = () => fire('portfolio-playbook-close-pricing-drawer');
const closePeek = () => fire('portfolio-playbook-close-peek');
const closeFoodDrawer = () => fire('portfolio-playbook-close-food-drawer');

// Open Maya's plan in the detail view if the list is still showing.
const ensurePlanOpen = () => {
  if (!document.querySelector('[data-tour="playbook-plan-meals"]')) {
    document.querySelector('[data-tour="playbook-plan-view"]')?.click();
  }
};

const setFirstMealExpanded = (expanded) => {
  window.setTimeout(() => {
    const btn = document.querySelector('[data-tour="playbook-plan-meal-expand"]');
    if (btn && (btn.getAttribute('aria-expanded') === 'true') !== expanded) btn.click();
  }, 200);
};

/*
 * Mascot clips are looked up by step id. `videoId: null` hides the clip on steps
 * whose narration no longer matches the UI (re-record, then drop the override).
 */
export function buildSteps({ language, hasClients, canSeeCompany, showAssignments }) {
  const he = language === 'hebrew';
  const phase = {
    dashboard: he ? 'לוח בקרה' : 'Dashboard',
    client: he ? 'לקוח' : 'Client',
    chat: he ? 'צ׳אט' : 'Chat',
    nutrition: he ? 'תזונה' : 'Nutrition',
    training: he ? 'אימון' : 'Training',
    analytics: he ? 'מעקב' : 'Progress',
    grow: he ? 'צמיחה' : 'Grow',
  };
  const nothingToClick = he
    ? 'אין צורך ללחוץ כאן — המשיכו עם הבא'
    : 'Nothing to click here — press Next to continue';
  const glanceOnly = he
    ? 'רק מבט — המשיכו עם הבא'
    : 'Glance only — press Next to continue';

  const sectionTotal = hasClients ? 7 : 2;
  const makeSectionIntro = ({
    id,
    sectionIndex,
    phaseLabel,
    title,
    description,
    route,
    icon,
    prepare,
  }) => ({
    id,
    mode: 'story',
    sectionCard: true,
    sectionIndex,
    sectionTotal,
    route,
    phase: phaseLabel,
    icon,
    title,
    description,
    prepare,
    nextLabel: he ? 'המשך' : 'Continue',
  });

  const assignmentsNote = showAssignments
    ? he
      ? ' מנהלים יכולים גם לשייך את הלקוח מחדש מתוך המבט המהיר.'
      : ' Managers can also reassign the client from the quick look.'
    : '';

  // ── 1. Dashboard ────────────────────────────────────────────────────────────
  const scanSteps = [
    makeSectionIntro({
      id: 'section-dashboard',
      sectionIndex: 1,
      phaseLabel: phase.dashboard,
      icon: 'dashboard',
      route: DASHBOARD_PATH,
      title: he ? 'לוח הבקרה' : 'Your dashboard',
      description: he
        ? 'נתחיל בתמונה הכללית: רשימת הלקוחות, עם לשוניות, חיפוש וסינון. משם נבחר לקוחת דוגמה ונמשיך איתה.'
        : 'We start with the big picture: your client list, sliced by tabs, search and filters. Then we pick a tour client and stay with her.',
    }),
    {
      id: 'scan-command-center',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-filters',
      position: 'bottom',
      phase: phase.dashboard,
      waitForDashboard: true,
      videoId: null,
      prepare: () => {
        closePeek();
        fire('portfolio-playbook-reset-dashboard-filters');
      },
      spotlightLabel: he ? 'לשוניות וסינון' : 'Tabs and filters',
      title: he ? 'מרכז הפיקוד' : 'Your command center',
      description: he
        ? 'הלשוניות מסננות את הרשימה בלחיצה: דורשים התייחסות, פעילים, שקטים, דוממים, בקליטה — וליד כל אחת מספר. אחר כך צמצמו עם חיפוש וסינון עד שהלקוחות הנכונים עולים.'
        : 'Tabs slice the list in one click: Needs attention, Active, Quiet, Silent, Onboarding, each with a count. Then narrow with search and Filter until the right clients surface.',
      proTip: he
        ? 'התחילו כל בוקר בלשונית "דורשים התייחסות" — דקה כאן חוסכת שעות של רדיפה אחר כך.'
        : 'Start every morning on "Needs attention" — a minute here saves hours of chasing later.',
    },
    {
      id: 'scan-table-intel',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-table-intel',
      position: 'bottom',
      phase: phase.dashboard,
      prepare: closePeek,
      title: he ? 'מודיעין ברשימה' : 'List intel',
      spotlightLabel: he ? 'שורת לקוח' : 'Client row',
      description: he
        ? `כל שורה מראה פעילות אחרונה, תוכניות, תשלום ואת הצעד הבא.${assignmentsNote}`
        : `Each row shows last activity, plans, payment and the next step.${assignmentsNote}`,
      proTip: he
        ? 'השורה המסומנת היא Maya — לקוחת הדוגמה. כל שאר הסיור ירוץ עליה.'
        : 'The highlighted row is Maya — your tour example. The rest of the tour runs on her.',
      continueHint: nothingToClick,
    },
  ];

  // ── 2. Client ───────────────────────────────────────────────────────────────
  const actSteps = [
    makeSectionIntro({
      id: 'section-client',
      sectionIndex: 2,
      phaseLabel: phase.client,
      icon: 'client',
      route: DASHBOARD_PATH,
      prepare: closePeek,
      title: he ? 'הלקוח' : 'The client',
      description: he
        ? 'נפתח את Maya — מבט מהיר מהרשימה, ואז מרחב העבודה המלא שלה.'
        : "We open Maya — a quick look from the list, then her full workspace.",
    }),
    {
      id: 'act-peek',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-table-intel',
      position: 'bottom',
      phase: phase.client,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'act-actions',
      advanceDelayMs: 350,
      videoId: null,
      prepare: closePeek,
      spotlightLabel: he ? 'Maya' : 'Maya',
      title: he ? 'פתיחת מבט מהיר' : 'Open the quick look',
      description: he
        ? 'לחיצה על שורה פותחת מבט מהיר בצד — בלי לעזוב את הרשימה. לחיצה כפולה פותחת את הלקוח ישירות.'
        : 'Clicking a row opens a quick look on the side — without leaving the list. Double-click opens the client directly.',
      clickLabel: he ? 'לחצו על השורה של Maya' : "Click Maya's row",
      clickLabelMobile: he ? 'הקישו על השורה של Maya' : "Tap Maya's row",
    },
    {
      id: 'act-actions',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-action-view',
      targetMobile: 'playbook-action-view',
      secondaryTarget: 'playbook-quick-look',
      panelDock: 'top',
      phase: phase.client,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'client-hero',
      // Fallback when the row click was skipped with Next.
      prepare: () => fire('portfolio-playbook-open-peek'),
      title: he ? 'המבט המהיר' : 'The quick look',
      description: he
        ? 'הצעד הבא, פעילות אחרונה, שקילות, סטטוס תוכניות, הודעות ותשלום — במבט אחד. לחצו "פתיחת הלקוח" כדי להיכנס למרחב העבודה של Maya.'
        : "Next step, last activity, weigh-ins, plan status, messages and payment — at a glance. Click Open client to enter Maya's workspace.",
      spotlightLabel: he ? 'פתיחת הלקוח' : 'Open client',
      clickLabel: he ? 'לחצו "פתיחת הלקוח"' : 'Click Open client',
      clickLabelMobile: he ? 'הקישו "פתיחת הלקוח"' : 'Tap Open client',
    },
    {
      id: 'client-hero',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-client-hero',
      position: 'bottom',
      phase: phase.client,
      title: he ? 'במבט אחד' : 'At a glance',
      spotlightLabel: he ? 'נתוני הלקוחה' : 'Client metrics',
      description: he
        ? 'משקל התחלה ועכשיו, יעד קלוריות מול תחזוקה, BMI על סקאלה, והמטרה של Maya. החלטה במבט אחד.'
        : "Maya's start and current weight, calorie target vs. maintenance, BMI on a colour scale, and her goal. Decide at a glance.",
      continueHint: nothingToClick,
    },
    {
      id: 'client-profile',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-client-profile',
      clickTarget: 'playbook-client-profile-toggle',
      position: 'bottom',
      phase: phase.client,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'client-profile-expanded',
      advanceDelayMs: 550,
      title: he ? 'פרופיל לקוח' : 'Client profile',
      description: he
        ? 'לחצו על החץ ב"פרטים אישיים" כדי לפתוח.'
        : 'Click the arrow on Personal Details to open it.',
      spotlightLabel: he ? 'פתח פרופיל' : 'Expand profile',
      clickLabel: he ? 'לחצו על החץ' : 'Click the arrow',
    },
    {
      id: 'client-profile-expanded',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-client-profile-expanded',
      position: 'bottom',
      phase: phase.client,
      prepare: () => fire('portfolio-playbook-expand-profile'),
      title: he ? 'הפרופיל נפתח' : 'Profile expanded',
      spotlightLabel: he ? 'פרטים אישיים' : 'Personal details',
      description: he
        ? 'שם, שפה, תאריך לידה ועוד — מה שמאמנים בודקים לפני כל תוכנית.'
        : 'Name, language, date of birth, and more — what coaches review before every plan.',
      proTip: he
        ? 'למטה: יצירת קשר ומיקום, נתונים פיזיים, תזונה ורפואה, חלון זמני ארוחות. "ערוך" למעלה משנה כל אחד מהם.'
        : 'Below: Contact & Location, Physical Stats, Dietary & Medical, Meal Time Window. Edit (top right) changes any of them.',
      continueHint: nothingToClick,
    },
    {
      id: 'client-workspace-tabs',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-workspace-tabs',
      position: 'bottom',
      phase: phase.client,
      videoId: null,
      prepare: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
      spotlightLabel: he ? 'לשוניות הלקוח' : 'Client tabs',
      title: he ? 'מרחב העבודה של הלקוח' : 'Client workspace',
      description: he
        ? 'כל מה שקשור ל-Maya נמצא בלשוניות: סקירה, צ׳אט, תוכנית תזונה, תוכנית אימון, יומן אכילה ואנליטיקה. נעבור עליהן לפי הסדר.'
        : "Everything about Maya lives in these tabs: Overview, Chat, Nutrition Plan, Training Plan, Food Logs and Analytics. We'll walk them in order.",
      continueHint: nothingToClick,
    },
    {
      id: 'client-switcher',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-topbar-client-btn',
      position: 'bottom',
      phase: phase.client,
      videoId: null,
      spotlightLabel: he ? 'החלף לקוח' : 'Switch Client',
      title: he ? 'החלפת לקוח' : 'Switch client',
      description: he
        ? 'מעבר לכל לקוח מכל עמוד, עם חיפוש לפי שם, אימייל, טלפון או קוד. הלשונית הפתוחה נשארת — רק הלקוח מתחלף.'
        : 'Jump to any client from any page. Search by name, email, phone or code. You stay on the same tab — only the client changes.',
      proTip: he ? 'קיצור מקלדת: Ctrl/⌘ + K.' : 'Keyboard shortcut: Ctrl/⌘ + K.',
      continueHint: glanceOnly,
    },
  ];

  // ── 3. Chat ─────────────────────────────────────────────────────────────────
  const connectSteps = [
    makeSectionIntro({
      id: 'section-chat',
      sectionIndex: 3,
      phaseLabel: phase.chat,
      icon: 'chat',
      route: CLIENT_DASHBOARD_PATH,
      title: he ? 'צ׳אט' : 'Chat',
      description: he
        ? 'ציר זמן אחד לכל מה ש-Maya עושה — הודעות, ארוחות ושקילות. נפתח אותו מתמונת המצב של הלקוחה.'
        : 'One timeline for everything Maya does — messages, meals and weigh-ins. We open it from her client snapshot.',
    }),
    {
      id: 'client-quick-actions',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-client-snapshot',
      secondaryTarget: 'playbook-quick-actions',
      panelPosition: 'top',
      position: 'top',
      phase: phase.chat,
      prepare: () => {
        closeTopbarClientMenu();
        const el = document.querySelector('[data-tour="playbook-client-snapshot"]');
        scrollTourTargetIntoView(el, { panelEdge: 'top', panelSize: 230 });
      },
      title: he ? 'תמונת מצב' : 'Client snapshot',
      spotlightLabel: he ? 'תמונת מצב' : 'Client snapshot',
      description: he
        ? 'תפריטים, שיחות ומשקל מאז השקילה הראשונה (עם סימון בתוכנית / סוטה מהתוכנית), ושני קיצורים: צור תפריט ופתח צ׳אט.'
        : 'Plans, chats and weight since the first weigh-in (with an On plan / Off plan flag), plus two shortcuts: Create meal plan and Open chat.',
      continueHint: he
        ? 'המשיכו עם הבא כדי לסמן את הצ׳אט'
        : 'Press Next to spotlight Open chat',
    },
    {
      id: 'to-chat',
      mode: 'spotlight',
      route: CLIENT_DASHBOARD_PATH,
      target: 'playbook-go-chat',
      panelPosition: 'top',
      position: 'top',
      phase: phase.chat,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'chat-thread',
      advanceDelayMs: 400,
      prepare: () => {
        closeTopbarClientMenu();
        const el = document.querySelector('[data-tour="playbook-go-chat"]');
        scrollTourTargetIntoView(el, { panelEdge: 'top', panelSize: 230 });
      },
      title: he ? 'פתיחת הצ׳אט' : 'Open the chat',
      description: he
        ? 'לחצו "פתח צ׳אט" כדי לראות את ציר הזמן של Maya.'
        : "Click Open chat to see Maya's timeline.",
      spotlightLabel: he ? 'פתח צ׳אט' : 'Open chat',
      clickLabel: he ? 'לחצו על "פתח צ׳אט"' : 'Click "Open chat"',
    },
    {
      id: 'chat-thread',
      mode: 'spotlight',
      route: CHAT_PATH,
      target: 'playbook-chat-thread',
      panelDock: 'side',
      scrollFriendly: true,
      softSpotlight: true,
      phase: phase.chat,
      videoId: null,
      spotlightLabel: he ? 'ציר זמן' : 'Timeline',
      title: he ? 'כל התמונה במקום אחד' : 'The full picture',
      description: he
        ? 'הודעות, ארוחות ושקילות לפי סדר הזמן — כולל מה שה-AI כבר ענה. התצוגה לקריאה בלבד.'
        : 'Messages, logged meals and weigh-ins in time order — including what the AI already answered. This view is read-only.',
      continueHint: nothingToClick,
    },
    {
      id: 'chat-day-header',
      mode: 'spotlight',
      route: CHAT_PATH,
      target: 'playbook-chat-day-header',
      position: 'bottom',
      phase: phase.chat,
      videoId: null,
      spotlightLabel: he ? 'סיכום יומי' : 'Daily totals',
      title: he ? 'סיכום יומי' : 'Daily totals',
      description: he
        ? 'כל יום נפתח בקלוריות מול היעד של Maya ובחלבון / פחמימות / שומן. אדום = חריגה מהיעד.'
        : "Each day starts with calories against Maya's target and her protein / carbs / fat. Red means she's over.",
      continueHint: nothingToClick,
    },
    {
      id: 'chat-food-open',
      mode: 'spotlight',
      route: CHAT_PATH,
      target: 'playbook-chat-food-card',
      position: 'top',
      phase: phase.chat,
      interactive: true,
      followNativeAction: true,
      allowManualAdvance: true,
      advanceToStepId: 'chat-food-drawer',
      advanceDelayMs: 400,
      videoId: null,
      prepare: closeFoodDrawer,
      spotlightLabel: he ? 'ארוחה מתועדת' : 'Logged meal',
      title: he ? 'פתיחת ארוחה מתועדת' : 'Open a logged meal',
      description: he
        ? 'כל ארוחה ש-Maya מתעדת מופיעה ככרטיס. לחצו כדי לראות את הפירוט המלא.'
        : 'Every meal Maya logs shows up as a card. Click it for the full breakdown.',
      clickLabel: he ? 'לחצו על כרטיס הארוחה' : 'Click the meal card',
    },
    {
      id: 'chat-food-drawer',
      mode: 'spotlight',
      route: CHAT_PATH,
      target: 'playbook-chat-food-drawer',
      panelDock: 'side',
      softSpotlight: true,
      scrollFriendly: true,
      phase: phase.chat,
      videoId: null,
      prepare: () => {
        if (!document.querySelector('[data-tour="playbook-chat-food-drawer"]')) {
          document.querySelector('[data-tour="playbook-chat-food-card"]')?.click();
        }
      },
      spotlightLabel: he ? 'פירוט הארוחה' : 'Meal breakdown',
      title: he ? 'פירוט הארוחה' : 'Meal breakdown',
      description: he
        ? 'קלוריות ומאקרו מול היעד, ציוני התאמה לתוכנית ובריאות, וכל פריט שה-AI זיהה בתמונה.'
        : 'Calories and macros against target, plan-fit and health scores, and every item the AI recognised.',
      continueHint: he
        ? 'רק מבט — הבא יסגור וימשיך'
        : 'Glance only — Next closes this and continues',
    },
  ];

  // ── 4. Nutrition ────────────────────────────────────────────────────────────
  const nourishSteps = [
    makeSectionIntro({
      id: 'section-nutrition',
      sectionIndex: 4,
      phaseLabel: phase.nutrition,
      icon: 'nutrition',
      route: CHAT_PATH,
      prepare: closeFoodDrawer,
      title: he ? 'תזונה' : 'Nutrition',
      description: he
        ? 'כאן מנהלים את תוכניות הארוחות של Maya — מהרשימה, דרך הארוחות והמרכיבים, ועד מה שהיא רואה באפליקציה.'
        : "This is where you manage Maya's meal plans — from the list, through meals and ingredients, to what she sees in her app.",
    }),
    {
      id: 'to-plans',
      mode: 'spotlight',
      route: CHAT_PATH,
      target: 'playbook-topbar-meal-plans',
      targetMobile: 'playbook-topbar-meal-plans',
      panelDock: 'center',
      position: 'bottom',
      phase: phase.nutrition,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'plan-list',
      advanceDelayMs: 350,
      prepare: closeFoodDrawer,
      spotlightLabel: he ? 'תוכנית תזונה' : 'Nutrition Plan',
      title: he ? 'מרכז התזונה' : 'Nutrition hub',
      description: he
        ? 'עברו ללשונית "תוכנית תזונה" במרחב הלקוח.'
        : 'Open the Nutrition Plan tab in the client workspace.',
      clickLabel: he ? 'לחצו על "תוכנית תזונה"' : 'Click Nutrition Plan',
      clickLabelMobile: he ? 'הקישו על "תוכנית תזונה"' : 'Tap Nutrition Plan',
    },
    {
      id: 'plan-list',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-view',
      secondaryTarget: 'playbook-plan-list',
      position: 'bottom',
      phase: phase.nutrition,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'plan-summary',
      advanceDelayMs: 500,
      allowManualAdvance: true,
      spotlightLabel: he ? 'צפה' : 'View',
      title: he ? 'כרטיסי התוכניות' : 'Plan cards',
      description: he
        ? 'כל כרטיס מראה סטטוס, טווח תאריכים ופעולות. נפתח את התוכנית הפעילה של Maya כדי לראות איך נראה יום מאוזן.'
        : "Each card shows status, date range and actions. We'll open Maya's active plan to see what a balanced day looks like.",
      clickLabel: he ? 'לחצו על "צפה"' : 'Click View',
      proTip: he
        ? 'ירוק = פעילה, כחול = מתוזמנת, אדום = פגה, כתום = טיוטה.'
        : 'Green = active, blue = scheduled, red = expired, amber = draft.',
    },
    {
      id: 'plan-summary',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-summary',
      position: 'bottom',
      phase: phase.nutrition,
      scrollFriendly: true,
      prepare: () => {
        // View may still be loading (Hebrew translation) — don't click it twice.
        if (!document.querySelector('[data-tour="playbook-plan-summary"]')) {
          window.setTimeout(() => {
            if (!document.querySelector('[data-tour="playbook-plan-summary"]')) {
              document.querySelector('[data-tour="playbook-plan-view"]')?.click();
            }
          }, 900);
        }
      },
      spotlightLabel: he ? 'סיכום יומי' : 'Daily summary',
      title: he ? 'היום במבט אחד' : 'The day at a glance',
      description: he
        ? 'סיכום כל המנות העיקריות: סה״כ קלוריות, גרם חלבון / פחמימות / שומן ופיצול המאקרו.'
        : 'Adds up every main dish: total calories, protein / carbs / fat grams and the macro split.',
      continueHint: nothingToClick,
    },
    {
      id: 'plan-meals',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-meals',
      position: 'top',
      phase: phase.nutrition,
      scrollFriendly: true,
      prepare: ensurePlanOpen,
      spotlightLabel: he ? 'ארוחות' : 'Meals',
      title: he ? 'הארוחות של היום' : "The day's meals",
      description: he
        ? 'כל משבצת ארוחה מציגה מנה עם קלוריות ומאקרו. לחיצה על שם המנה עורכת אותו.'
        : 'Each meal slot shows its dish with calories and macros. Click a dish name to edit it.',
      continueHint: nothingToClick,
    },
    {
      id: 'plan-meal-swap',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-meal-move',
      position: 'bottom',
      phase: phase.nutrition,
      videoId: null,
      prepare: () => {
        ensurePlanOpen();
        setFirstMealExpanded(false);
      },
      spotlightLabel: he ? 'החלפת מנות' : 'Swap dishes',
      title: he ? 'החלפת מנות בין ארוחות' : 'Swap dishes between meals',
      description: he
        ? 'גררו מנה בידית אל ארוחה אחרת, או בחרו "העבר אל…". המשבצות נשארות במקומן; "שמור" שומר את השינוי.'
        : 'Drag a dish by its handle onto another meal, or pick Move to…. The meal slots stay put; Save Changes keeps it.',
      continueHint: glanceOnly,
    },
    {
      id: 'plan-meal-alts',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-meal-expand',
      secondaryTarget: 'playbook-plan-meal-card',
      position: 'top',
      phase: phase.nutrition,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'plan-meal-expanded',
      advanceDelayMs: 450,
      allowManualAdvance: true,
      prepare: () => {
        ensurePlanOpen();
        // Keep the first meal collapsed so the expand arrow is the obvious control.
        setFirstMealExpanded(false);
      },
      spotlightLabel: he ? 'פתח ארוחה' : 'Expand meal',
      title: he ? 'פתיחת ארוחה' : 'Open a meal',
      description: he
        ? 'לחצו על החץ כדי לפתוח את הארוחה — בפנים: מרכיבים וחלופות.'
        : 'Click the arrow to expand this meal — ingredients and alternatives are inside.',
      clickLabel: he ? 'לחצו על החץ' : 'Click the down arrow',
    },
    {
      // Keeps its id for the existing mascot clip and expand timing.
      id: 'plan-meal-expanded',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-meal-ingredients',
      targetFallback: 'playbook-plan-meal-card',
      position: 'top',
      phase: phase.nutrition,
      scrollFriendly: true,
      prepare: () => {
        ensurePlanOpen();
        setFirstMealExpanded(true);
      },
      spotlightLabel: he ? 'מרכיבים' : 'Ingredients',
      title: he ? 'מה בפנים' : "What's inside",
      description: he
        ? 'מרכיבים עם כמויות וערכים תזונתיים. לחיצה על שם מרכיב מחליפה אותו.'
        : 'Ingredients with portions and macros. Click an ingredient name to replace it.',
      continueHint: nothingToClick,
    },
    {
      id: 'plan-meal-alternatives',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-meal-alts',
      position: 'top',
      phase: phase.nutrition,
      scrollFriendly: true,
      videoId: null,
      prepare: () => {
        ensurePlanOpen();
        setFirstMealExpanded(true);
      },
      spotlightLabel: he ? 'ארוחות חלופיות' : 'Alternative meals',
      title: he ? 'חלופות' : 'Alternatives',
      description: he
        ? 'החלפות ש-Maya יכולה לבחור באותם יעדים — גמישות בלי לצאת מהמאקרו.'
        : 'Swaps Maya can pick that hit the same targets — flexibility without leaving her macros.',
      continueHint: nothingToClick,
    },
    {
      id: 'plan-actions',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-plan-actions',
      position: 'bottom',
      phase: phase.nutrition,
      scrollFriendly: true,
      videoId: null,
      prepare: () => {
        setFirstMealExpanded(false);
        if (!document.querySelector('[data-tour="playbook-plan-actions"]')) {
          document.querySelector('[data-tour="playbook-plan-view"]')?.click();
        }
      },
      spotlightLabel: he ? 'פעולות' : 'Actions',
      title: he ? 'שליטה בתוכנית' : 'Plan controls',
      description: he
        ? 'עריכת תזמון, השבתה, שמירת עריכות או חזרה לרשימה. התוכנית הפעילה היא מה ש-Maya רואה באפליקציה.'
        : 'Edit the schedule, deactivate, save your edits or go back to the list. The active plan is what Maya sees in her app.',
      continueHint: nothingToClick,
    },
  ];

  // ── 5. Training ─────────────────────────────────────────────────────────────
  const trainSteps = [
    makeSectionIntro({
      id: 'section-training',
      sectionIndex: 5,
      phaseLabel: phase.training,
      icon: 'training',
      route: MEAL_PLANS_PATH,
      title: he ? 'אימון' : 'Training',
      description: he
        ? 'תוכניות האימון של Maya במקום אחד — נראה את התוכנית הפעילה ואיך בונים חדשה.'
        : "Maya's workout plans in one place — see the live plan and how to build a new one.",
    }),
    {
      id: 'to-training',
      mode: 'spotlight',
      route: MEAL_PLANS_PATH,
      target: 'playbook-topbar-training',
      targetMobile: 'playbook-topbar-training',
      panelDock: 'center',
      position: 'bottom',
      phase: phase.training,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'training-plans',
      advanceDelayMs: 350,
      spotlightLabel: he ? 'תוכנית אימון' : 'Training Plan',
      title: he ? 'גם אימונים' : 'Training too',
      description: he
        ? 'עברו ללשונית "תוכנית אימון" במרחב הלקוח.'
        : 'Open the Training Plan tab in the client workspace.',
      clickLabel: he ? 'לחצו על "תוכנית אימון"' : 'Click Training Plan',
      clickLabelMobile: he ? 'הקישו על "תוכנית אימון"' : 'Tap Training Plan',
    },
    {
      id: 'training-plans',
      mode: 'spotlight',
      route: TRAINING_PATH,
      target: 'playbook-training-current',
      position: 'bottom',
      phase: phase.training,
      scrollFriendly: true,
      videoId: null,
      spotlightLabel: he ? 'התוכנית הנוכחית' : 'Current plan',
      title: he ? 'התוכנית הפעילה' : 'The live plan',
      description: he
        ? 'תוכנית האימון הפעילה של Maya — מטרה, משך, רמה וסטטוס. אפשר לצפות ולערוך אותה או להשהות אותה.'
        : "Maya's live training plan — goal, duration, level and status. View & Edit it, or pause it.",
      proTip: he
        ? 'תוכניות ישנות וטיוטות נשמרות תחת "תוכניות קודמות וטיוטות".'
        : 'Older and draft plans stay under "Past & Draft Plans".',
      continueHint: nothingToClick,
    },
    {
      id: 'training-create',
      mode: 'spotlight',
      route: TRAINING_PATH,
      target: 'playbook-training-create',
      position: 'bottom',
      phase: phase.training,
      videoId: null,
      spotlightLabel: he ? 'צור תוכנית אימון' : 'Create training plan',
      title: he ? 'בניית תוכנית חדשה' : 'Build a new plan',
      description: he
        ? 'פותח את בונה התוכניות: פרטי תוכנית (שם, תאריך התחלה, מספר שבועות), ואז רשת שבועית של אימונים ותרגילים, ולבסוף סקירה ושמירה. בסיור לא נפתח אותו כדי לא ליצור תוכנית אמיתית.'
        : "Opens the plan builder: plan info (name, start date, number of weeks), then a weekly grid of workouts and exercises, then Review & Save. We won't open it in the tour so nothing real gets created.",
      continueHint: glanceOnly,
    },
  ];

  // ── 6. Food logs and analytics ──────────────────────────────────────────────
  const measureSteps = [
    makeSectionIntro({
      id: 'section-analytics',
      sectionIndex: 6,
      phaseLabel: phase.analytics,
      icon: 'analytics',
      route: TRAINING_PATH,
      title: he ? 'יומן ואנליטיקה' : 'Food logs and analytics',
      description: he
        ? 'מה Maya באמת אכלה, ואיך הגוף והתזונה שלה משתנים לאורך זמן.'
        : 'What Maya actually ate, and how her body and nutrition trend over time.',
    }),
    {
      id: 'to-foodlogs',
      mode: 'spotlight',
      route: TRAINING_PATH,
      target: 'playbook-workspace-tab-foodlogs',
      targetMobile: 'playbook-workspace-tab-foodlogs',
      panelDock: 'center',
      position: 'bottom',
      phase: phase.analytics,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'foodlogs-day',
      advanceDelayMs: 350,
      videoId: null,
      spotlightLabel: he ? 'יומן אכילה' : 'Food Logs',
      title: he ? 'יומן האכילה' : 'The food log',
      description: he
        ? 'כל ארוחה ש-Maya צילמה או תיעדה, יום אחר יום. פתחו את יומן האכילה.'
        : 'Every meal Maya photographed or logged, day by day. Open Food Logs.',
      clickLabel: he ? 'לחצו על "יומן אכילה"' : 'Click Food Logs',
      clickLabelMobile: he ? 'הקישו על "יומן אכילה"' : 'Tap Food Logs',
    },
    {
      id: 'foodlogs-day',
      mode: 'spotlight',
      route: FOODLOGS_PATH,
      target: 'playbook-foodlogs-day',
      position: 'bottom',
      phase: phase.analytics,
      scrollFriendly: true,
      videoId: null,
      spotlightLabel: he ? 'יום אחד' : 'One day',
      title: he ? 'יום אחר יום' : 'Day by day',
      description: he
        ? 'כל יום מציג סך קלוריות ומאקרו מול היעד; פתחו ארוחה לראות תמונה, פריטים וציוני בריאות והתאמה לתוכנית.'
        : 'Each day shows total calories and macros against target; open a meal to see its photo, items, and health and plan-fit scores.',
      continueHint: nothingToClick,
    },
    {
      id: 'to-analytics',
      mode: 'spotlight',
      route: FOODLOGS_PATH,
      target: 'playbook-topbar-analytics',
      targetMobile: 'playbook-topbar-analytics',
      panelDock: 'center',
      position: 'bottom',
      phase: phase.analytics,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'analytics-tabs',
      advanceDelayMs: 350,
      videoId: null,
      prepare: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
      spotlightLabel: he ? 'אנליטיקה' : 'Analytics',
      title: he ? 'המספרים מספרים' : 'Numbers tell the story',
      description: he
        ? 'מגמות גוף ותזונה לאורך זמן. פתחו את האנליטיקה מלשוניות מרחב הלקוח.'
        : 'Body and nutrition trends over time. Open Analytics from the client workspace tabs.',
      clickLabel: he ? 'לחצו על "אנליטיקה"' : 'Click Analytics',
      clickLabelMobile: he ? 'הקישו על "אנליטיקה"' : 'Tap Analytics',
    },
    {
      id: 'analytics-tabs',
      mode: 'spotlight',
      route: ANALYTICS_PATH,
      target: 'playbook-analytics-tabs',
      position: 'bottom',
      phase: phase.analytics,
      scrollFriendly: true,
      videoId: null,
      spotlightLabel: he ? 'שני מבטים' : 'Two lenses',
      title: he ? 'תזונה וגוף' : 'Nutrition and body',
      description: he
        ? 'אנליטיקת תזונה מציגה קלוריות ומאקרו יומיים מול היעדים. אנליטיקת גוף עוקבת אחר משקל, אחוז שומן והיקפים. בשתיהן יש מסנני טווח.'
        : "Nutrition analytics charts daily calories and macros against the client's targets. Body analytics tracks weight, body fat and circumferences. Both have range filters.",
      proTip: he
        ? 'אפשר לרשום מדידות חדשות ישר מאנליטיקת הגוף ("הוסף מדידה").'
        : 'You can log new measurements right from Body analytics (Add Measurement).',
      continueHint: nothingToClick,
    },
    {
      id: 'analytics-overview',
      mode: 'spotlight',
      route: ANALYTICS_PATH,
      target: 'playbook-analytics-export',
      targetFallback: 'playbook-analytics-page',
      position: 'top',
      phase: phase.analytics,
      scrollFriendly: true,
      videoId: null,
      spotlightLabel: he ? 'ייצוא לאקסל' : 'Export to Excel',
      title: he ? 'מוכן לצ׳ק אין' : 'Ready for check-ins',
      description: he
        ? 'ייצוא מדידות הגוף או יומן המזון לאקסל — לחודש, 3, 6, שנה או טווח מותאם.'
        : 'Export body measurements or food logs to Excel — for 1, 3 or 6 months, a year, or a custom range.',
      continueHint: nothingToClick,
    },
  ];

  // ── 7. Grow ─────────────────────────────────────────────────────────────────
  const growSectionIntro = makeSectionIntro({
    id: 'section-grow',
    sectionIndex: hasClients ? 7 : 2,
    phaseLabel: phase.grow,
    icon: 'grow',
    route: hasClients ? ANALYTICS_PATH : DASHBOARD_PATH,
    title: he ? 'צמיחה' : 'Grow',
    description: he
      ? canSeeCompany
        ? 'חוזרים לקליניקה: הזמנת לקוחות, ניהול הקליניקה ויצירת מוצר מחיר — ואז סיום הסיור.'
        : 'חוזרים לקליניקה: איך מזמינים לקוחות חדשים — ואז סיום הסיור.'
      : canSeeCompany
        ? 'Back to your practice: inviting clients, practice management and creating a priced product — then we wrap up.'
        : 'Back to your practice: how to bring in new clients — then we wrap up.',
  });

  // Leaving the client workspace is a real gesture worth teaching.
  const backToPracticeStep = {
    id: 'to-practice',
    mode: 'spotlight',
    route: ANALYTICS_PATH,
    target: 'playbook-workspace-back',
    position: 'bottom',
    phase: phase.grow,
    interactive: true,
    followNativeAction: true,
    advanceToStepId: 'grow-referral',
    advanceDelayMs: 350,
    videoId: null,
    prepare: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    spotlightLabel: he ? 'חזרה לסקירה' : 'Back to overview',
    title: he ? 'חזרה לקליניקה' : 'Back to your practice',
    description: he
      ? 'סיימתם עם Maya? החץ הזה יוצא מהלקוחה וחוזר לרשימת הלקוחות.'
      : 'Done with Maya? This arrow leaves the client and returns to your client list.',
    clickLabel: he ? 'לחצו על החץ' : 'Click the back arrow',
  };

  const growReferralSteps = [
    {
      id: 'grow-referral',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-referral',
      position: 'bottom',
      phase: phase.grow,
      interactive: true,
      followNativeAction: true,
      advanceToStepId: 'grow-referral-dialog',
      advanceDelayMs: 350,
      prepare: closePeek,
      spotlightLabel: he ? 'הזמנת לקוחות' : 'Invite clients',
      title: he ? 'הגדילו את התיק' : 'Grow your book',
      description: he
        ? 'מכאן מזמינים לקוחות חדשים — קישורים וקודי גישה. לחצו לפתיחת החלון.'
        : 'Invite new clients from here — links and access codes. Click to open the panel.',
      clickLabel: he ? 'לחצו "הזמנת לקוחות"' : 'Click Invite clients',
    },
    {
      id: 'grow-referral-dialog',
      mode: 'spotlight',
      route: DASHBOARD_PATH,
      target: 'playbook-referral-tabs',
      panelDock: 'side',
      position: 'left',
      phase: phase.grow,
      scrollFriendly: true,
      prepare: () => fire('portfolio-playbook-open-referral'),
      spotlightLabel: he ? 'פשוט · מוגבל · קוד' : 'Simple · Limited · Code',
      title: he ? 'שלוש דרכים להזמין' : 'Three ways to invite',
      description: he
        ? 'קישור פשוט — קבוע ללא הגבלה. קישור מוגבל — מכסה ותפוגה. צור קוד — קוד גישה לחודש חינם בבוט ה-AI.'
        : 'Simple Link — permanent, unlimited. Limited Link — max clients and expiry. Generate code — a 6-digit code for a free month of the AI bot.',
      continueHint: he
        ? 'רק מבט — הבא יסגור וימשיך'
        : 'Glance only — Next closes this and continues',
    },
  ];

  const growCompanySteps = canSeeCompany
    ? [
        {
          id: 'grow-company',
          mode: 'spotlight',
          route: DASHBOARD_PATH,
          // Spotlight the management links in the top bar. Clicks go to Pricing.
          target: 'playbook-topbar-company-menu',
          targetMobile: 'playbook-topbar-company-menu',
          clickTarget: 'playbook-topbar-company-pricing',
          panelDock: 'center',
          position: 'bottom',
          phase: phase.grow,
          interactive: true,
          followNativeAction: true,
          advanceToStepId: 'grow-company-pricing',
          advanceDelayMs: 450,
          prepare: () => {
            closeReferralDialog();
            openTopbarNavMenu('company');
          },
          spotlightLabel: he ? 'ניהול' : 'Management',
          title: he ? 'ניהול הקליניקה' : 'Practice management',
          description: he
            ? 'למנהלים בלבד. צוות והזמנות — עובדים, תפקידים והזמנות. תמחור והרשמה — מוצרים, קופונים ושאלות הרשמה. דף נחיתה — מה לקוחות חדשים רואים. לחצו "תמחור והרשמה" להמשך.'
            : 'Managers only. Team & Invites — staff, roles and invitations. Pricing & Signup — products, promos and signup questions. Landing Page — what new clients see. Click Pricing & Signup to continue.',
          clickLabel: he ? 'לחצו "תמחור והרשמה"' : 'Click Pricing & Signup',
        },
        {
          id: 'grow-company-pricing',
          mode: 'spotlight',
          route: MANAGER_DASHBOARD_PATH,
          target: 'playbook-company-pricing-tabs',
          panelPosition: 'bottom',
          position: 'bottom',
          phase: phase.grow,
          softSpotlight: true,
          scrollFriendly: true,
          prepare: closePricingDrawer,
          spotlightLabel: he ? 'תמחור · קודי הנחה · הצטרפות' : 'Pricing · Promo Codes · Onboarding',
          title: he ? 'תמחור והרשמה' : 'Pricing & Signup',
          description: he
            ? 'שלוש לשוניות: תמחור (מוצרים), קודי הנחה, והצטרפות (שאלות הרשמה) — מה שהלקוחות רואים כשהם נרשמים.'
            : 'Three tabs: Pricing (products), Promo Codes, and Onboarding (signup questions) — what clients see when they sign up.',
          continueHint: glanceOnly,
        },
        {
          id: 'grow-company-new-product',
          mode: 'spotlight',
          route: MANAGER_DASHBOARD_PATH,
          target: 'playbook-company-new-product',
          panelDock: 'center',
          position: 'bottom',
          phase: phase.grow,
          interactive: true,
          followNativeAction: true,
          advanceToStepId: 'grow-company-product-drawer',
          advanceDelayMs: 400,
          prepare: () => {
            closePricingDrawer();
            fire('portfolio-playbook-activate-pricing-tab');
          },
          spotlightLabel: he ? 'מוצר חדש' : 'New product',
          title: he ? 'הוסיפו תוכנית מחיר' : 'Add a priced plan',
          description: he
            ? 'בלשונית תמחור — כאן יוצרים מוצר שהלקוחות רואים בהרשמה. לחצו "מוצר חדש".'
            : 'On the Pricing tab — create a plan clients see at signup. Click New product.',
          clickLabel: he ? 'לחצו "מוצר חדש"' : 'Click New product',
        },
        {
          id: 'grow-company-product-drawer',
          mode: 'spotlight',
          route: MANAGER_DASHBOARD_PATH,
          target: 'playbook-company-product-drawer',
          panelDock: 'side',
          position: 'left',
          phase: phase.grow,
          softSpotlight: true,
          scrollFriendly: true,
          prepare: () => fire('portfolio-playbook-open-pricing-drawer'),
          spotlightLabel: he ? 'טופס מוצר' : 'Product form',
          title: he ? 'טופס המוצר החדש' : 'The new-product form',
          description: he
            ? 'שם התוכנית, לוגו, מחיר (USD), חיוב חודשי/שנתי, קטגוריה (תזונה / אימון / אחר) ותיאור. בסיור לא נשמור — רק מכירים את השדות.'
            : "Plan name, logo, price (USD), monthly/yearly billing, category (nutrition / training / other) and description. We won't save in the tour — just learn the fields.",
          continueHint: he
            ? 'רק מבט — הבא יסגור וימשיך'
            : 'Glance only — Next closes this and continues',
          proTip: he
            ? 'אחרי יצירה המוצר מופיע גם בקודי ההנחה ובהרשמה.'
            : 'After you create one, it also shows up in promo codes and signup.',
        },
      ]
    : [];

  const notificationsStep = {
    id: 'notifications-bell',
    mode: 'spotlight',
    route: DASHBOARD_PATH,
    target: 'playbook-topbar-notifications',
    position: 'bottom',
    phase: phase.grow,
    videoId: null,
    prepare: () => {
      closeReferralDialog();
      closePricingDrawer();
      closePeek();
    },
    spotlightLabel: he ? 'התראות' : 'Notifications',
    title: he ? 'התראות' : 'Notifications',
    description: he
      ? 'אירועים של לקוחות שדורשים את תשומת הלב שלכם מגיעים לכאן.'
      : 'Client events that need your attention land here.',
    continueHint: nothingToClick,
  };

  const growFinaleStep = {
    id: 'grow-platform-map',
    mode: 'story',
    route: DASHBOARD_PATH,
    phase: phase.grow,
    prepare: () => {
      closeReferralDialog();
      closePricingDrawer();
    },
    title: he ? 'סיימתם — אתם מוכנים' : "You're set",
    description: he
      ? 'תוכניות תזונה, אימונים, יומן אכילה ואנליטיקה — בלשוניות של כל לקוח; התראות — מאחורי הפעמון. התחילו כל יום ברשימת הלקוחות.'
      : "Nutrition plans, training, food logs and analytics live in each client's tabs; notifications sit behind the bell. Start each day on your client list.",
  };

  const growSteps = [
    growSectionIntro,
    ...(hasClients ? [backToPracticeStep] : []),
    ...growReferralSteps,
    ...growCompanySteps,
    notificationsStep,
    growFinaleStep,
  ];

  if (!hasClients) {
    return [...scanSteps, ...growSteps];
  }

  return [
    ...scanSteps,
    ...actSteps,
    ...connectSteps,
    ...nourishSteps,
    ...trainSteps,
    ...measureSteps,
    ...growSteps,
  ];
}
